import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import RichText from 'components/RichText';

import useInView from 'hooks/useInView';

import BREAKPOINTS from 'styles/breakpoints';
import COLORS from 'styles/colors';
import { DotsPattern } from 'styles/global';
import TYPO from 'styles/typography';

import { map, clamp, lerp } from 'utils/helpers';

const Section = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(
    ${COLORS.pinkMuted.opacity(0.5)} 88%,
    ${COLORS.sky} 12%
  );
  color: ${COLORS.velvety};
  transition: background-color 0.6s;
  overflow-x: hidden;

  ${BREAKPOINTS.small`
    ${DotsPattern}
    background-color: var(--background);
    clip-path: inset(0 0 0 0);
  `}
`;

const TitleContainer = styled.div`
  margin: 0 auto;
  position: relative;
  width: 80%;
  max-width: 800px;
  min-height: 330px;
`;

const Title = styled(RichText)`
  margin: 0 auto;
  padding: 50px 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 0;
  text-align: center;

  h2 {
    position: relative;
    color: var(--heading-color);
    transition: 0.6s ease;
    z-index: 100;
  }

  * {
    ${TYPO.h3}
  }

  u {
    color: ${COLORS.dawn};
    color: var(--underline-color);
  }

  &:nth-child(2) {
    z-index: 100;

    h2 {
      opacity: 0;

      &:last-child {
        opacity: 1;
      }
    }
  }
`;

const ImageContainer = styled.div`
  position: var(--position);
  top: -260px;
  left: -335px;
  width: 100%;
  max-width: 670px;
  height: 400px;
  border-radius: 5px;
  background-color: ${COLORS.black};
  clip-path: var(--clip-path);
  transition: clip-path 0.6s cubic-bezier(0.84, 0.01, 0.23, 1);
  transform: translate(50vw, 50vh);
  overflow: hidden;
  transform: var(--img-transition);
`;

const ImageLeft = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: inherit;
  height: inherit;
  background-image: ${({ bgImg }) => (bgImg ? `url(${bgImg})` : 'none')};
  background-size: 100% auto;
  opacity: var(--img-l-opacity);
  transition: 0.4s;
`;

const ImageRight = styled(ImageLeft)`
  opacity: var(--img-r-opacity);
`;

const pos = { x: 0, y: 0 };
let posCache = pos;
let direction = { x: posCache.x - pos.x, y: posCache.y - pos.y };

const progress = { x: 0, y: 0, amt: 6 };
const translate = { x: 0, y: 0 };
const rotation = { previous: 0, current: 0, amt: 0.08 };

let firstAnimationCycle = false;
let animationFrameId;

const resetStyles = (root) => {
  root.style.setProperty('--clip-path', 'inset(50% 0px 50% 0px)');
  root.style.setProperty('--heading-color', COLORS.velvety);
  root.style.setProperty('--underline-color', COLORS.dawn);
  root.style.setProperty('--background', COLORS.pinkMuted.opacity(0.5));
};

const FoodAndDrinkDesktop = ({ title, images = [] }) => {
  const titleRef = useRef(null);
  const [inViewRef, inView] = useInView(false);

  const [imageLeft, imageRight] = images;

  useEffect(() => {
    const root = window.document.documentElement;
    const ref = titleRef.current;

    if (!inView) {
      cancelAnimationFrame(animationFrameId);
      resetStyles(root);
    }

    const mouseMove = (e) => {
      pos.x = e.clientX;
      pos.y = e.clientY;
    };

    function animateImage() {
      // Update mouse position values
      const mouseDistanceX = clamp(Math.abs(posCache.x - pos.x), 0, 100);
      direction = { x: posCache.x - pos.x, y: posCache.y - pos.y };
      posCache = { x: pos.x, y: pos.y };

      // Get previous and current rotate values
      rotation.current = firstAnimationCycle
        ? 0
        : map(mouseDistanceX, 0, 100, 0, direction.x < 0 ? 60 : -60);

      rotation.previous = firstAnimationCycle
        ? rotation.current
        : lerp(rotation.previous, rotation.current, rotation.amt);

      if (rotation.previous < 0) {
        root.style.setProperty('--background', COLORS.redWine);
        root.style.setProperty('--img-l-opacity', 0);
        root.style.setProperty('--img-r-opacity', 1);
      } else {
        root.style.setProperty('--background', COLORS.purpleDark);
        root.style.setProperty('--img-l-opacity', 1);
        root.style.setProperty('--img-r-opacity', 0);
      }

      // Get x and y translate values
      progress.x = pos.x - translate.x;
      translate.x += progress.x / progress.amt;

      progress.y = pos.y - translate.y;
      translate.y += progress.y / progress.amt;

      root.style.setProperty(
        '--img-transition',
        `translate(${translate.x}px, ${translate.y}px) rotate(${rotation.previous}deg)`
      );

      firstAnimationCycle = false;
      animationFrameId = requestAnimationFrame(animateImage);
    }

    const mouseenter = () => {
      animationFrameId = requestAnimationFrame(animateImage);
      firstAnimationCycle = true;
      ref.addEventListener('mousemove', mouseMove);

      root.style.setProperty('--clip-path', 'inset(0 0px 0 0px)');
      root.style.setProperty('--heading-color', COLORS.lilac);
      root.style.setProperty('--underline-color', COLORS.pinkMillennial);
    };

    const mouseleave = () => {
      cancelAnimationFrame(animationFrameId);
      ref.removeEventListener('mousemove', mouseMove);
      resetStyles(root);
    };

    if (inView) {
      ref.addEventListener('mouseenter', mouseenter);
      ref.addEventListener('mouseleave', mouseleave);
    }
    return () => {
      ref.removeEventListener('mouseenter', mouseenter);
      ref.removeEventListener('mouseleave', mouseleave);
    };
  }, [inView]);

  return (
    <Section ref={inViewRef} style={{ '--background': null }}>
      <TitleContainer
        ref={titleRef}
        style={{ '--heading-color': null, '--underline-color': null }}
      >
        <Title html={title?.html} />
        <Title html={title?.html} />
      </TitleContainer>

      <ImageContainer
        images={images}
        style={{
          '--img-transition': null,
          '--clip-path': null,
          '--position': inView ? 'fixed' : 'static',
        }}
      >
        <ImageLeft
          bgImg={imageLeft?.url ?? ''}
          style={{ '--img-l-opacity': null }}
        />
        <ImageRight
          bgImg={imageRight?.url ?? ''}
          style={{ '--img-r-opacity': null }}
        />
      </ImageContainer>
    </Section>
  );
};

export default FoodAndDrinkDesktop;
